// export.ts — CSV export of a collection's items (all local; nothing leaves the device).
// Columns: the built-in item fields + one column per custom attribute the collection defines.

import type { Collection, Item, CollectionSet } from "./store";
import { formatAttr } from "./store";

function cell(v: string | number): string {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function csv(headers: string[], rows: (string | number)[][]): string {
  return [headers, ...rows].map((r) => r.map(cell).join(",")).join("\n");
}

function slug(s: string): string {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "collection";
}

/**
 * Full CSV for one collection. Built-in columns, then one column per schema field,
 * then notes. `cost` is exported as the value the user logged (never an appraisal).
 */
export function itemsCsv(collection: Collection, items: Item[], sets: CollectionSet[]): string {
  const setName = new Map(sets.map((s) => [s.id, s.name]));
  const fields = collection.attr_schema ?? [];
  const headers = [
    "Name", "Status", "Acquired", "Source", "Logged value", "Condition", "Set",
    ...fields.map((f) => f.label),
    "Notes",
  ];
  const rows = items.map((it) => [
    it.name,
    it.wishlist ? "Wishlist" : "Have",
    it.acquired_on || "",
    it.source || "",
    it.cost === null || it.cost === undefined ? "" : it.cost,
    it.condition || "",
    it.set_id ? setName.get(it.set_id) ?? "" : "",
    ...fields.map((f) => formatAttr(it.attrs?.[f.key])),
    it.notes || "",
  ]);
  return csv(headers, rows);
}

export function buildExport(collection: Collection, items: Item[], sets: CollectionSet[]): { csv: string; filename: string } {
  const stamp = new Date().toISOString().slice(0, 10);
  return { csv: itemsCsv(collection, items, sets), filename: `${slug(collection.name)}-${stamp}.csv` };
}
